/**
 * Reading the money off a view.
 *
 * Separated from the drawing for the usual reason: a chip that fails to render
 * is obvious, and a pot read off the wrong field is a confident wrong number
 * sitting in the middle of the table.
 *
 * Everything here answers "what did the game say", never "what should poker
 * do". The field NAMES come from the layout, so the renderer stays ignorant of
 * poker and a second betting game declares its own and is drawn for free.
 */

import type { GameLayoutWire } from '../net/session.js';
import type { MatchView } from '../net/match.js';

/** The pot, or null when this game has no money or has not said where it is. */
export function potOf(view: MatchView, layout: GameLayoutWire): number | null {
  const field = layout.chips?.pot;
  if (field === undefined) return null;
  const value = view.view[field];
  return typeof value === 'number' ? value : null;
}

/**
 * What one seat has put in this betting round, or zero.
 *
 * Zero rather than null for a missing entry: a seat that has not bet and a
 * seat the game said nothing about look the same on the felt, and both draw
 * nothing.
 */
export function committedOf(view: MatchView, layout: GameLayoutWire, seat: number): number {
  const field = layout.chips?.committed;
  if (field === undefined) return 0;
  const value = view.view[field];
  if (!Array.isArray(value)) return 0;
  const amount = value[seat];
  return typeof amount === 'number' && amount > 0 ? amount : 0;
}

/**
 * The pot as it stood BEFORE the bets currently in front of the seats.
 *
 * Poker's `pot` is the whole of the money at stake, which includes what is
 * still sitting in front of the players this round - so drawing both put the
 * same chips on the table twice, and a heads-up pot of 40 read as 40 in the
 * middle plus 20 and 20 on the felt. The middle shows what has been swept in;
 * the rest is still in front of whoever bet it, which is exactly how a real
 * table looks mid-round.
 */
export function middleOf(view: MatchView, layout: GameLayoutWire, seats: number): number {
  const pot = potOf(view, layout);
  if (pot === null) return 0;
  let outstanding = 0;
  for (let seat = 0; seat < seats; seat++) outstanding += committedOf(view, layout, seat);
  return Math.max(0, pot - outstanding);
}
